// TopNavigation.tsx
// Fix: When AppBar is `position="fixed"`, it overlays page content.
// Solution: Render a "spacer" element AFTER the AppBar (only when AppBar is shown)
// so the page content starts BELOW the fixed header on every page automatically.

import React from 'react'
import AppBar from '@material-ui/core/AppBar'
import Grid from '@material-ui/core/Grid'
import Hidden from '@material-ui/core/Hidden'
import IconButton from '@material-ui/core/IconButton'
import LinearProgress from '@material-ui/core/LinearProgress'
import Toolbar from '@material-ui/core/Toolbar'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import MenuIcon from '@material-ui/icons/Menu'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import { uiSettings } from 'etc/settings'
import { useDispatch, useSelector } from 'react-redux'
import { toggleMobileMenu } from 'reducers/stateReducer'
import { IRootReduxState } from 'types/reducers'
import Branding from './branding'
import { useLocation } from 'react-router-dom'
import { UserModel } from 'data/models'
import ErrorBoundary from 'components/ErrorBoundary'
import { useIntl } from 'react-intl'

// =====================
// Styles
// =====================
const useStyles = makeStyles((theme) => ({
    signin: {
        color: uiSettings.header.foreground,
        borderColor: uiSettings.header.foreground,
    },

    // The fixed AppBar (white bar)
    appBar: (props: any) => ({
        padding: '0px 2px',
        zIndex: theme.zIndex.drawer + 1,
        boxShadow:
            theme.palette.type === 'light'
                ? '0px 2px 5px 0px #dadada'
                : '0px 2px 5px 0px #1b1b1b',
        backgroundColor: theme.palette.background.paper,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
    }),

    // When side drawer is expanded on big screens
    appBarShift: (props: any) => ({
        marginLeft: uiSettings.drawerWidth,
        width: props.bigScreen ? `calc(100% - ${uiSettings.drawerWidth}px)` : '100%',
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),

    // Your toolbar styling (inside AppBar)
    toolbar: (props: any) => ({
        height: '100%',
        paddingLeft: '0px',
        paddingRight: '0px',
        '@media (max-width: 601px)': {
            minHeight: '56px',
        },
        '@media (max-width: 600px) and (orientation: landscape)': {
            minHeight: '56px',
        },
    }),
}))

// =====================
// Top progress bar
// =====================
const BorderLinearProgress = withStyles({
    root: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: 1203, // above the appbar
    },
    bar: {},
})(LinearProgress)

// =====================
// Props
// =====================
interface Props {
    componentsLeft?: JSX.Element
    componentsRight?: JSX.Element

    /** @deprecated */
    title?: string
    /** @deprecated */
    loading?: number
    /** @deprecated */
    user?: UserModel
    /** @deprecated */
    expanded?: boolean
    /** @deprecated */
    mobileMenuToggle?: () => void
    /** @deprecated */
    toggleExpanded?: () => void
}

// =====================
// Component
// =====================
const TopNavigation: React.FC<Props> = ({ componentsLeft, componentsRight }) => {
    const intl = useIntl()
    const loading = useSelector((state: IRootReduxState) => state.app.state.loading)
    const expanded = useSelector((state: IRootReduxState) => state.app.state.sideMenuExpanded)

    const bigScreen = useMediaQuery('(min-width:960px)')
    const smallScreen = useMediaQuery('(max-width:600px)')

    const dispatch = useDispatch()
    const openMenuAria = intl.formatMessage({
        id: 'navigation.openDrawer',
        defaultMessage: 'Open menu',
    })
    const classes = useStyles({ bigScreen })
    const location = useLocation()

    // Header always renders so every module has the hamburger (right-side on
    // mobile / left-side on desktop-narrow) to open the side menu — even when
    // the page passes no toolbar components.
    const shouldShowHeader = true

    return (
        <ErrorBoundary>
            {/* Loading indicator at very top */}
            <BorderLinearProgress hidden={loading === 0} />

            {/* ✅ Only render AppBar when needed */}
            {shouldShowHeader && (
                <>
                    <AppBar
                        position="fixed"
                        className={clsx(classes.appBar, {
                            [classes.appBarShift]: expanded,
                        })}
                    >
                        <Toolbar className={classes.toolbar}>
                            {smallScreen ? (
                                // Mobile layout
                                <Grid container alignItems="center" justify="space-between" direction="row" wrap="nowrap">
                                    {/* Branding only on root path */}
                                    {location.pathname.length <= 1 && (
                                        <Grid item>
                                            <div style={{ marginLeft: '3px' }}>
                                                <Branding width={200} />
                                            </div>
                                        </Grid>
                                    )}

                                    {/* Left actions */}
                                    {componentsLeft && (
                                        <Grid item container justify="flex-start" alignItems="center">
                                            {componentsLeft}
                                        </Grid>
                                    )}

                                    {/* Right actions */}
                                    {componentsRight && (
                                        <Grid item container justify="center" alignItems="center">
                                            {componentsRight}
                                        </Grid>
                                    )}

                                    {/* Mobile menu button */}
                                    <Grid item container justify="flex-end" alignItems="center" xs={2}>
                                        <Grid item>
                                            <IconButton aria-label={openMenuAria} onClick={() => dispatch(toggleMobileMenu())}>
                                                <MenuIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            ) : (
                                // Desktop layout
                                <Grid container alignItems="center" justify="space-between" wrap="nowrap" spacing={3}>
                                    {/* Hamburger only for mdDown */}
                                    <Grid item>
                                        <Hidden mdUp implementation="css">
                                            <IconButton aria-label={openMenuAria} onClick={() => dispatch(toggleMobileMenu())}>
                                                <MenuIcon />
                                            </IconButton>
                                        </Hidden>
                                    </Grid>

                                    {/* Left actions */}
                                    {componentsLeft && <Grid item>{componentsLeft}</Grid>}

                                    {/* Center spacer */}
                                    <Grid item style={{ flexGrow: 1 }}>
                                        <div />
                                    </Grid>

                                    {/* Right actions */}
                                    {componentsRight && <Grid item>{componentsRight}</Grid>}
                                </Grid>
                            )}
                        </Toolbar>
                    </AppBar>
                </>
            )}
        </ErrorBoundary>
    )
}

export default TopNavigation